import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ApiError } from '@/api/httpClient'
import { useTabsStore } from '@/stores/tabsStore'
import { useProjectsStore } from '@/stores/projectsStore'
import { useDraftStore } from '@/stores/draftStore'
import { useDiagramStore } from '@/stores/diagramStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useModalStore } from '@/hooks/useModalStore'
import { Dialog } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { morphToast } from '@/components/ui/Toast'
import { CloseIcon, EditIcon } from '@/components/ui/icons'
import type { ProjectTab } from '@/types'

const DIALECT_BADGE: Record<string, string> = {
  postgresql: 'PG',
  mysql:      'MySQL',
  mariadb:    'Maria',
  sqlserver:  'MSSQL',
  oracle:     'Oracle',
  sqlite:     'SQLite',
}

function getOpenVersionMeta(tab: ProjectTab) {
  const versions = Array.isArray(tab.versions.projectVersions)
    ? tab.versions.projectVersions
    : []
  const currentVersionId = tab.versions.currentVersionId

  if (!currentVersionId || versions.length === 0) {
    return null
  }

  const currentVersion = versions.find((version) => version.id === currentVersionId)
  if (!currentVersion) {
    return null
  }

  const latestVersion = versions.reduce((latest, version) => {
    if (!latest) return version
    return new Date(version.timestamp).getTime() > new Date(latest.timestamp).getTime()
      ? version
      : latest
  }, versions[0])

  return {
    name: currentVersion.name,
    isLatest: latestVersion?.id === currentVersion.id,
  }
}

// ── Tab edit popover ──────────────────────────────────────────────────────────

interface TabEditPopoverProps {
  tab:      ProjectTab
  anchorEl: HTMLElement
  onClose:  () => void
}

function TabEditPopover({ tab, anchorEl, onClose }: TabEditPopoverProps) {
  const { t } = useTranslation()
  const updateTab      = useTabsStore(s => s.updateTab)
  const renameProject = useProjectsStore((s) => s.rename)
  const currentProjectId = useDraftStore((s) => s.currentProjectId)
  const setCurrentProjectName = useDraftStore((s) => s.setCurrentProjectName)
  const [name, setName]       = useState(tab.title)
  const [nameError, setNameError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)
  const inputRef   = useRef<HTMLInputElement>(null)
  const remoteProjectId = Number(tab.projectId)
  const canPersist = Number.isInteger(remoteProjectId) && remoteProjectId > 0

  // Compute initial position below the anchor tab
  const anchorRect = anchorEl.getBoundingClientRect()
  const [pos, setPos] = useState({ top: anchorRect.bottom + 4, left: anchorRect.left, visible: false })

  // After mount, correct horizontal position so the popover never overflows the right edge
  useLayoutEffect(() => {
    if (!popoverRef.current) return
    const mw = popoverRef.current.offsetWidth
    const vw = window.innerWidth
    const pad = 8
    let left = anchorRect.left
    if (left + mw > vw - pad) {
      left = anchorRect.right - mw
    }
    left = Math.max(pad, Math.min(left, vw - mw - pad))
    setPos({ top: anchorRect.bottom + 4, left, visible: true })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Close on outside pointerdown
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (
        e.target instanceof Element &&
        (e.target.closest('[data-morph-select-menu="true"]') ||
          e.target.closest('[data-overlay-root="true"]'))
      ) {
        return
      }
      if (!popoverRef.current?.contains(e.target as Node)) onClose()
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [onClose])

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setNameError(t('newProject.nameRequired'))
      return
    }
    if (!canPersist) {
      morphToast.error(t('common.error', 'Este projeto ainda não pode ser editado aqui.'))
      return
    }

    setSubmitting(true)
    try {
      const updated = await renameProject(remoteProjectId, trimmed)
      updateTab(tab.id, { title: updated.name, remoteName: updated.name })
      if (currentProjectId === remoteProjectId) {
        useDiagramStore.getState().setProjectName(updated.name)
        setCurrentProjectName(updated.name)
      }
      morphToast.success(t('common.saved', 'Salvo.'))
      onClose()
    } catch (err) {
      if (err instanceof ApiError && err.code === 'PROJECT_NAME_DUPLICATE') {
        setNameError(t('newProject.duplicate', 'Já existe um projeto com este nome neste workspace.'))
        return
      }
      morphToast.error(err instanceof Error ? err.message : t('common.error', 'Erro ao salvar'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter')  { e.preventDefault(); void handleSave() }
    if (e.key === 'Escape') { e.preventDefault(); onClose() }
  }

  return (
    <div
      ref={popoverRef}
      className="z-50 w-[260px] rounded-xl border p-3 shadow-2xl"
      style={{
        position:   'fixed',
        top:        pos.top,
        left:       pos.left,
        visibility: pos.visible ? 'visible' : 'hidden',
        background:  'var(--bg-popup)',
        borderColor: 'var(--border)',
        color:       'var(--text)',
      }}
      onKeyDown={handleKeyDown}
    >
      <div className="mb-2 text-[11px] font-semibold" style={{ color: 'var(--text-dim)' }}>
        {t('tabs.editTab')}
      </div>

      {/* Name */}
      <label className="mb-0.5 block text-[10px]" style={{ color: 'var(--text-dim)' }}>
        {t('tabs.tabNameLabel')}
      </label>
      <input
        ref={inputRef}
        value={name}
        onChange={e => { setName(e.target.value); setNameError(null) }}
        className="mb-1 w-full rounded-md border px-2 py-1 text-[11px] outline-none"
        style={{
          background:  'var(--bg-input, var(--bg-card))',
          borderColor: nameError ? 'var(--color-danger)' : 'var(--border)',
          color:       'var(--text)',
        }}
        spellCheck={false}
        disabled={submitting}
      />
      {nameError ? (
        <div className="mb-1.5 text-[10px]" style={{ color: 'var(--color-danger)' }}>
          {nameError}
        </div>
      ) : null}

      <div className="mb-3" />

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={submitting}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={() => void handleSave()} disabled={submitting}>
          {submitting ? t('common.loading', 'Aguarde...') : t('common.save')}
        </Button>
      </div>
    </div>
  )
}

// ── Botão "+" com mini-menu ───────────────────────────────────────────────────

interface NewTabMenuPos {
  top: number
  left: number
  visible: boolean
}

function NewTabMenu({ atLimit }: { atLimit: boolean }) {
  const { t } = useTranslation()
  const [menuPos, setMenuPos] = useState<NewTabMenuPos | null>(null)
  const btnRef    = useRef<HTMLButtonElement>(null)
  const menuRef   = useRef<HTMLDivElement>(null)
  const openSqlImport = useModalStore(s => s.openSqlImport)
  const openNewProject = useModalStore(s => s.openNewProject)
  const openOpenProjectPicker = useModalStore(s => s.openOpenProjectPicker)

  const open = menuPos !== null

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent) => {
      if (
        !btnRef.current?.contains(e.target as Node) &&
        !menuRef.current?.contains(e.target as Node)
      ) {
        setMenuPos(null)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  // After menu renders (invisible), correct position so it never overflows the viewport
  useLayoutEffect(() => {
    if (!open || !menuRef.current || !btnRef.current) return
    const menu = menuRef.current
    const btn  = btnRef.current.getBoundingClientRect()
    const mw   = menu.offsetWidth
    const vw   = window.innerWidth
    const pad  = 8
    let left = btn.left
    if (left + mw > vw - pad) {
      left = btn.right - mw  // right-align to the button's right edge
    }
    left = Math.max(pad, Math.min(left, vw - mw - pad))
    setMenuPos(prev => prev ? { ...prev, left, visible: true } : null)
  }, [open])

  const handleToggle = () => {
    if (atLimit) return
    if (open) { setMenuPos(null); return }
    const rect = btnRef.current?.getBoundingClientRect()
    if (rect) setMenuPos({ top: rect.bottom + 4, left: rect.left, visible: false })
  }

  const handleNewProject = () => {
    setMenuPos(null)
    openNewProject()
  }

  const handleOpenProject = () => {
    setMenuPos(null)
    openOpenProjectPicker()
  }

  const handleImport = () => {
    setMenuPos(null)
    openSqlImport('new-project-menu')
  }

  return (
    <>
      <div className="ml-3 mr-2 flex shrink-0 items-center">
        <button
          ref={btnRef}
          type="button"
          onClick={handleToggle}
          disabled={atLimit}
          title={atLimit ? t('tabs.limitReached', 'Limite de 10 abas atingido') : t('tabs.newTab')}
          className="flex h-6 w-6 items-center justify-center rounded-md border text-sm font-medium leading-none transition-colors"
          style={{
            borderColor: atLimit ? 'var(--border)' : open ? 'var(--accent)' : 'var(--border)',
            background:  open && !atLimit ? 'var(--bg-hover)' : 'var(--bg-card)',
            color:       atLimit ? 'var(--text-dim)' : open ? 'var(--accent)' : 'var(--text-dim)',
            opacity:     atLimit ? 0.4 : 1,
            cursor:      atLimit ? 'not-allowed' : 'pointer',
          }}
          onMouseEnter={e => {
            if (!open && !atLimit) {
              e.currentTarget.style.borderColor = 'var(--accent)'
              e.currentTarget.style.color = 'var(--accent)'
            }
          }}
          onMouseLeave={e => {
            if (!open && !atLimit) {
              e.currentTarget.style.borderColor = 'var(--border)'
              e.currentTarget.style.color = 'var(--text-dim)'
            }
          }}
        >
          +
        </button>
      </div>

      {open && menuPos && (
        <div
          ref={menuRef}
          className="min-w-[180px] rounded-xl border py-1 shadow-lg"
          style={{
            position:   'fixed',
            top:        menuPos.top,
            left:       menuPos.left,
            visibility: menuPos.visible ? 'visible' : 'hidden',
            zIndex:      9999,
            background:  'var(--bg-card)',
            borderColor: 'var(--border)',
          }}
        >
          <button
            type="button"
            onClick={handleNewProject}
            className="w-full px-3 py-2 text-left text-[12px] transition-colors"
            style={{ color: 'var(--text)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            {t('tabs.emptyNew')}
          </button>
          <button
            type="button"
            onClick={handleOpenProject}
            className="w-full px-3 py-2 text-left text-[12px] transition-colors"
            style={{ color: 'var(--text)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            {t('tabs.openProject', 'Abrir projeto')}
          </button>
          <button
            type="button"
            onClick={handleImport}
            className="w-full px-3 py-2 text-left text-[12px] transition-colors"
            style={{ color: 'var(--text)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            {t('tabs.emptyImport')}
          </button>
        </div>
      )}
    </>
  )
}

const MAX_OPEN_TABS = 10

// ── TabBar ────────────────────────────────────────────────────────────────────

export function TabBar() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const tabs        = useTabsStore(s => s.tabs)
  const activeTabId = useTabsStore(s => s.activeTabId)
  const activateTab = useTabsStore(s => s.activateTab)
  const closeTab    = useTabsStore(s => s.closeTab)
  const workspaces = useWorkspaceStore((s) => s.workspaces)
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const setCurrentProjectName = useDraftStore((s) => s.setCurrentProjectName)
  const setCurrentProjectId = useDraftStore((s) => s.setCurrentProjectId)

  const [pendingClose, setPendingClose]       = useState<ProjectTab | null>(null)
  const [editingTab, setEditingTab]           = useState<{ tab: ProjectTab; el: HTMLElement } | null>(null)
  const [contextMenu, setContextMenu]         = useState<{ tab: ProjectTab; x: number; y: number } | null>(null)
  const versionToastTimerRef = useRef<number | null>(null)

  const clearVersionToastTimer = () => {
    if (versionToastTimerRef.current !== null) {
      window.clearTimeout(versionToastTimerRef.current)
      versionToastTimerRef.current = null
    }
  }

  useEffect(() => () => clearVersionToastTimer(), [])

  if (tabs.length === 0) return null

  const handleClose = (e: React.MouseEvent, tab: ProjectTab) => {
    e.stopPropagation()
    if (tab.isDirty) {
      setPendingClose(tab)
    } else {
      closeTab(tab.id)
    }
  }

  const handleEditClick = (e: React.MouseEvent, tab: ProjectTab) => {
    e.stopPropagation()
    const tabBtn = (e.currentTarget as HTMLElement).closest('[data-tab-btn]') as HTMLElement | null
    setEditingTab({ tab, el: tabBtn ?? (e.currentTarget as HTMLElement) })
  }

  const handleContextMenu = (e: React.MouseEvent, tab: ProjectTab) => {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu({ tab, x: e.clientX, y: e.clientY })
  }

  const handleContextMenuEdit = () => {
    if (!contextMenu) return
    const tabBtn = document.querySelector(`[data-tab-btn="${contextMenu.tab.id}"]`) as HTMLElement | null
    setEditingTab({ tab: contextMenu.tab, el: tabBtn ?? document.body })
    setContextMenu(null)
  }

  const confirmClose = () => {
    if (pendingClose) closeTab(pendingClose.id)
    setPendingClose(null)
  }

  const handleActivateTab = (tab: ProjectTab) => {
    setCurrentProjectName(tab.remoteName ?? tab.title)
    const projectId = Number(tab.projectId)
    setCurrentProjectId(Number.isInteger(projectId) && projectId > 0 ? projectId : null)
    activateTab(tab.id)

    if (!Number.isInteger(projectId) || projectId <= 0) return

    const workspaceSlug = (
      tab.workspaceId != null
        ? workspaces.find((workspace) => workspace.id === tab.workspaceId)?.slug
        : currentWorkspace?.slug
    ) ?? currentWorkspace?.slug

    if (!workspaceSlug) return
    navigate(`/workspace/${workspaceSlug}/projects/${projectId}/editor`)
  }

  return (
    <>
      <div
        data-guide="tabs"
        className="flex min-h-0 shrink-0"
        style={{
          background:   'var(--bg-toolbar)',
          borderBottom: '1px solid var(--border)',
          overflow:     'hidden',
        }}
      >
        {tabs.map(tab => {
          const isActive = tab.id === activeTabId
          const badge    = DIALECT_BADGE[tab.dialect] ?? tab.dialect
          const openVersion = getOpenVersionMeta(tab)
          const versionStatusMessage = openVersion
            ? openVersion.isLatest
              ? t('tabs.versionCurrentToast', { version: openVersion.name })
              : t('tabs.versionLegacyToast', { version: openVersion.name })
            : ''

          return (
            <button
              key={tab.id}
              data-tab-btn={tab.id}
              type="button"
              onClick={() => handleActivateTab(tab)}
              onContextMenu={(e) => handleContextMenu(e, tab)}
              className="group relative flex cursor-pointer items-center gap-1.5 whitespace-nowrap border-r px-3 py-1.5 text-[11px] transition-colors"
              style={{
                borderColor:   'var(--border)',
                background:    isActive ? 'var(--bg-canvas)' : 'transparent',
                color:         isActive ? 'var(--text)' : 'var(--text-dim)',
                borderBottom:  isActive ? '2px solid var(--accent)' : '2px solid transparent',
                paddingBottom: isActive ? 'calc(0.375rem - 1px)' : '0.375rem',
                minWidth:      '80px',
                maxWidth:      '200px',
                flex:          '1 1 0',
              }}
              title={`${tab.remoteName ?? tab.title} · ${tab.dialect}`}
            >
              {/* Dirty indicator */}
              {tab.isDirty && (
                <span
                  className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ background: 'var(--accent)' }}
                  aria-label={t('tabs.unsaved')}
                />
              )}

              {/* Dialect badge */}
              <span
                className="shrink-0 rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide"
                style={{
                  background: 'var(--bg-card)',
                  color:      'var(--text-dim)',
                  border:     '1px solid var(--border)',
                }}
              >
                {badge}
              </span>

              {/* Title */}
              <span className="truncate" style={{ maxWidth: '120px' }}>
                {tab.remoteName ?? tab.title}
              </span>

              {openVersion ? (
                <span
                  className="shrink-0 rounded px-1 py-0.5 text-[9px] font-semibold tracking-wide"
                  style={{
                    background: openVersion.isLatest
                      ? 'var(--color-success-bg)'
                      : 'var(--color-warning-bg)',
                    color: openVersion.isLatest ? 'var(--color-success)' : 'var(--color-warning)',
                    border: `1px solid ${openVersion.isLatest ? 'var(--color-success-border)' : 'var(--color-warning-border)'}`,
                  }}
                  onMouseEnter={() => {
                    clearVersionToastTimer()
                    versionToastTimerRef.current = window.setTimeout(() => {
                      morphToast(versionStatusMessage)
                    }, 650)
                  }}
                  onMouseLeave={clearVersionToastTimer}
                  onFocus={() => {
                    clearVersionToastTimer()
                    versionToastTimerRef.current = window.setTimeout(() => {
                      morphToast(versionStatusMessage)
                    }, 650)
                  }}
                  onBlur={clearVersionToastTimer}
                >
                  {openVersion.name}
                </span>
              ) : null}

              {/* Close button */}
              <span
                role="button"
                aria-label={t('tabs.close', { title: tab.remoteName ?? tab.title })}
                onClick={(e) => handleClose(e, tab)}
                className="ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded text-sm leading-none transition-colors"
                style={{ color: 'var(--text-dim)' }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'var(--bg-hover)'
                  e.currentTarget.style.color = 'var(--text)'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent'
                  e.currentTarget.style.color = 'var(--text-dim)'
                }}
              >
                ×
              </span>
            </button>
          )
        })}

        {/* Botão "+" no final das abas */}
        <NewTabMenu atLimit={tabs.length >= MAX_OPEN_TABS} />
      </div>

      {/* Tab edit popover */}
      {editingTab && (
        <TabEditPopover
          tab={editingTab.tab}
          anchorEl={editingTab.el}
          onClose={() => setEditingTab(null)}
        />
      )}

      {/* Tab context menu */}
      {contextMenu && (
        <>
          <div
            className="fixed inset-0 z-40"
            onPointerDown={() => setContextMenu(null)}
          />
          <div
            className="fixed z-50 min-w-[140px] rounded-lg border py-1 shadow-xl"
            style={{
              top:         contextMenu.y,
              left:        contextMenu.x,
              background:  'var(--bg-popup)',
              borderColor: 'var(--border)',
              color:        'var(--text)',
            }}
          >
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-1.5 text-[12px] transition-colors"
              style={{ color: 'var(--text)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)' }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent' }}
              onClick={handleContextMenuEdit}
            >
              <span className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--text-dim)' }}><EditIcon /></span>
              {t('tabs.editTab')}
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-1.5 text-[12px] transition-colors"
              style={{ color: 'var(--color-danger)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)' }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent' }}
              onClick={() => { handleClose({ stopPropagation: () => {} } as React.MouseEvent, contextMenu.tab); setContextMenu(null) }}
            >
              <span className="h-3.5 w-3.5 shrink-0"><CloseIcon /></span>
              {t('tabs.close', { title: contextMenu.tab.remoteName ?? contextMenu.tab.title })}
            </button>
          </div>
        </>
      )}

      {/* Confirm close dirty tab */}
      <Dialog
        open={!!pendingClose}
        title={t('tabs.closeUnsavedTitle')}
        onClose={() => setPendingClose(null)}
        onConfirm={confirmClose}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setPendingClose(null)}>
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={confirmClose}>
              {t('tabs.closeAnyway')}
            </Button>
          </>
        )}
      >
        {t('tabs.closeUnsavedMessage', { title: pendingClose?.title ?? '' })}
      </Dialog>
    </>
  )
}
